<script setup>
/* global axios */
// Detalhe de uma execução de flow — meta, contato, nó atual, contexto (variáveis) e ações.
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const flowId = route.params.flowId;
const executionId = route.params.executionId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const ex = ref(null);
const loading = ref(true);
const resetting = ref(false);

// Rótulo resolvido reativo (t() no setup cairia no fallback en).
const STATUS_CLS = {
  running: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  waiting: 'bg-amber-100 text-amber-700 dark:bg-yellow-900/40 dark:text-yellow-300',
  waiting_response:
    'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-600 dark:bg-n-solid-3 dark:text-n-slate-11',
  paused: 'bg-gray-100 text-gray-600 dark:bg-n-solid-3 dark:text-n-slate-11',
};
const STATUS_KEY = {
  running: 'running',
  waiting: 'waiting',
  waiting_response: 'waiting',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
  paused: 'paused',
};

const load = async () => {
  loading.value = true;
  ex.value = (await axios.get(`${base}/flow_executions/${executionId}`)).data.data;
  loading.value = false;
};

const isActive = s => ['running', 'waiting', 'waiting_response'].includes(s);
const fmt = d => (d ? new Date(d).toLocaleString('pt-BR') : '—');

const statusInfo = computed(() => {
  const s = ex.value?.status;
  return {
    label: STATUS_KEY[s] ? t(`CHATBOT.history.status.${STATUS_KEY[s]}`) : s,
    cls:
      STATUS_CLS[s] ||
      'bg-gray-100 text-gray-600 dark:bg-n-solid-3 dark:text-n-slate-11',
  };
});

// Contexto (jsonb) → lista de pares chave/valor. Escalares mostram o valor;
// objetos/arrays mostram JSON formatado.
const contextEntries = computed(() => {
  const ctx = ex.value?.context || {};
  return Object.keys(ctx).map(k => {
    const v = ctx[k];
    const isObj = v !== null && typeof v === 'object';
    return {
      key: k,
      isObj,
      value: isObj ? JSON.stringify(v, null, 2) : String(v),
    };
  });
});

const resetExec = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('CHATBOT.history.resetConfirm'))) return;
  resetting.value = true;
  try {
    ex.value = (
      await axios.patch(`${base}/flow_executions/${executionId}/reset`)
    ).data.data;
  } finally {
    resetting.value = false;
  }
};
const pause = async () => {
  ex.value = (
    await axios.patch(`${base}/flow_executions/${executionId}/pause`)
  ).data.data;
  await load();
};
const resume = async () => {
  ex.value = (
    await axios.patch(`${base}/flow_executions/${executionId}/resume`)
  ).data.data;
  await load();
};
const cancel = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('CHATBOT.history.cancelConfirm'))) return;
  await axios.delete(`${base}/flow_executions/${executionId}`);
  await load();
};
const openConversation = () => {
  if (ex.value?.conversation_id)
    router.push(
      `/app/accounts/${accountId}/conversations/${ex.value.conversation_id}`
    );
};
const goBack = () =>
  router.push(`/app/accounts/${accountId}/flows/${flowId}/history`);

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-y-auto bg-n-background">
    <div class="sticky top-0 z-10 bg-n-background border-b border-n-weak">
      <div class="flex items-center h-16 gap-3 px-6">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="goBack"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class="w-4 h-4"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 18l-6-6 6-6" />
          </svg>
          {{ t('CHATBOT.history.detail.back') }}
        </button>
        <h1 class="text-heading-1 text-n-slate-12">
          {{ t('CHATBOT.history.detail.title') }}
          <span v-if="ex" class="text-n-slate-10">#{{ ex.id }}</span>
        </h1>
        <span
          v-if="ex"
          class="px-2 py-0.5 text-[0.625rem] rounded-full"
          :class="statusInfo.cls"
          >{{ statusInfo.label }}</span
        >

        <div class="ml-auto flex items-center gap-2">
          <button
            v-if="ex && ex.conversation_id"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="openConversation"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5 8.5 8.5 0 0 1 21 11.5z" />
            </svg>
            {{ t('CHATBOT.history.openConversation') }}
          </button>
          <button
            v-if="ex && isActive(ex.status)"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="pause"
          >
            <span class="i-lucide-pause w-4 h-4" />
            {{ t('SEQUENCES.executions.pause') }}
          </button>
          <button
            v-if="ex && ex.status === 'paused'"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-blue-11 bg-n-background hover:bg-n-alpha-1"
            @click="resume"
          >
            <span class="i-lucide-play w-4 h-4" />
            {{ t('SEQUENCES.executions.resume') }}
          </button>
          <button
            v-if="ex"
            :disabled="resetting"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-amber-11 bg-n-background hover:bg-n-alpha-1 disabled:opacity-60"
            @click="resetExec"
          >
            <span class="i-lucide-rotate-ccw w-4 h-4" />
            {{ t('CHATBOT.history.reset') }}
          </button>
          <button
            v-if="ex && (isActive(ex.status) || ex.status === 'paused')"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-ruby-11 bg-n-background hover:bg-n-alpha-1"
            @click="cancel"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4">
              <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
            {{ t('CHATBOT.history.actions.cancel') }}
          </button>
        </div>
      </div>
    </div>

    <div class="w-full px-6 py-6">
      <div v-if="loading" class="text-n-slate-11">
        {{ t('CHATBOT.history.loadingHistory') }}
      </div>
      <div v-else-if="!ex" class="py-12 text-center text-n-slate-10">
        {{ t('CHATBOT.history.detail.notFound') }}
      </div>
      <div v-else class="grid gap-6 lg:grid-cols-3">
        <!-- Coluna esquerda: dados gerais -->
        <div class="lg:col-span-2 flex flex-col gap-6">
          <!-- Erro em destaque -->
          <div
            v-if="ex.error_message"
            class="p-4 rounded-lg border border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11 text-sm"
          >
            <div class="font-medium mb-1">
              {{ t('CHATBOT.history.table.error') }}
            </div>
            {{ ex.error_message }}
          </div>

          <!-- Nó atual -->
          <div class="p-5 rounded-lg border border-n-weak bg-n-solid-1">
            <div class="text-[0.6875rem] uppercase text-n-slate-10 mb-2">
              {{ t('CHATBOT.history.detail.currentNode') }}
            </div>
            <div class="flex items-center gap-2">
              <span class="i-lucide-git-branch w-4 h-4 text-n-slate-11" />
              <span class="text-sm font-medium text-n-slate-12">
                {{ ex.current_node_label || ex.current_node_id || '—' }}
              </span>
              <span
                v-if="ex.current_node_type"
                class="px-2 py-0.5 text-[0.625rem] rounded-full bg-n-alpha-2 text-n-slate-11"
                >{{ ex.current_node_type }}</span
              >
            </div>
          </div>

          <!-- Contexto / variáveis -->
          <div class="rounded-lg border border-n-weak bg-n-solid-1 overflow-hidden">
            <div
              class="px-5 py-3 border-b border-n-weak text-[0.6875rem] uppercase text-n-slate-10"
            >
              {{ t('CHATBOT.history.detail.context') }}
            </div>
            <div
              v-if="!contextEntries.length"
              class="px-5 py-8 text-center text-n-slate-10 text-sm"
            >
              {{ t('CHATBOT.history.detail.noContext') }}
            </div>
            <div v-else class="divide-y divide-n-weak">
              <div
                v-for="entry in contextEntries"
                :key="entry.key"
                class="px-5 py-3 grid grid-cols-3 gap-3"
              >
                <div class="font-mono text-xs text-n-slate-11 break-all">
                  {{ entry.key }}
                </div>
                <div class="col-span-2">
                  <pre
                    v-if="entry.isObj"
                    class="text-xs whitespace-pre-wrap break-all text-n-slate-12 bg-n-alpha-1 rounded p-2"
                    >{{ entry.value }}</pre
                  >
                  <span v-else class="text-sm text-n-slate-12 break-all">{{
                    entry.value
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Coluna direita: meta -->
        <div class="flex flex-col gap-6">
          <div class="p-5 rounded-lg border border-n-weak bg-n-solid-1 flex flex-col gap-4">
            <div>
              <div class="text-[0.6875rem] uppercase text-n-slate-10 mb-1">
                {{ t('CHATBOT.history.table.contact') }}
              </div>
              <div class="text-sm font-medium text-n-slate-12">
                {{ ex.contact_name || '—' }}
              </div>
              <div v-if="ex.contact_phone" class="text-xs text-n-slate-10">
                {{ ex.contact_phone }}
              </div>
              <div v-if="ex.contact_email" class="text-xs text-n-slate-10">
                {{ ex.contact_email }}
              </div>
            </div>
            <div v-if="ex.conversation_id">
              <div class="text-[0.6875rem] uppercase text-n-slate-10 mb-1">
                {{ t('CHATBOT.history.detail.conversation') }}
              </div>
              <button
                class="text-sm text-n-blue-11 hover:underline"
                @click="openConversation"
              >
                #{{ ex.conversation_id }}
              </button>
            </div>
            <div>
              <div class="text-[0.6875rem] uppercase text-n-slate-10 mb-1">
                {{ t('CHATBOT.history.table.startTime') }}
              </div>
              <div class="text-sm text-n-slate-12">
                {{ fmt(ex.started_at || ex.created_at) }}
              </div>
            </div>
            <div>
              <div class="text-[0.6875rem] uppercase text-n-slate-10 mb-1">
                {{ t('CHATBOT.history.table.completion') }}
              </div>
              <div class="text-sm text-n-slate-12">{{ fmt(ex.completed_at) }}</div>
            </div>
            <div v-if="ex.next_run_at">
              <div class="text-[0.6875rem] uppercase text-n-slate-10 mb-1">
                {{ t('CHATBOT.history.detail.nextRun') }}
              </div>
              <div class="text-sm text-n-slate-12">{{ fmt(ex.next_run_at) }}</div>
            </div>
            <div>
              <div class="text-[0.6875rem] uppercase text-n-slate-10 mb-1">
                {{ t('CHATBOT.history.detail.flow') }}
              </div>
              <div class="text-sm text-n-slate-12">{{ ex.flow_name }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
